import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Logo } from '@/components/brand/logo';
import { BotonPantallaCompleta } from '@/components/ui/boton-pantalla-completa';
import { isOperatorShift } from '@/config/navigation';
import { useDocumentTitle } from '@/hooks/use-document-title';

import { selectIsAuthenticated, selectUser, useAuthStore } from '../auth.store';
import { ApiStatus } from '../components/api-status';
import { BrandPanel } from '../components/brand-panel';
import { LoginCard } from '../components/login-card';

interface LocationState {
  from?: string;
}

/**
 * Pantalla de inicio de sesión.
 *
 * - Si ya hay sesión activa, redirige al destino original o al dashboard.
 * - Recuerda la ruta intentada (`state.from`) para volver allí tras el login.
 */
export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const user = useAuthStore(selectUser);

  useDocumentTitle('Iniciar sesión');

  const requestedPath = (location.state as LocationState | null)?.from;
  const from =
    isOperatorShift(user?.groupIds) &&
    (!requestedPath || requestedPath === '/inicio' || requestedPath.startsWith('/inicio/'))
      ? '/turno'
      : requestedPath ?? '/inicio';

  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, from, navigate]);

  return (
    <div className="relative grid min-h-dvh lg:grid-cols-[1.1fr_minmax(0,1fr)]">
      <BrandPanel />

      <main className="relative flex flex-col items-center justify-center px-5 py-10 sm:px-8 lg:px-12">
        <BotonPantallaCompleta className="absolute right-4 top-4 hidden lg:flex" />

        <div className="w-full max-w-md animate-fade-up">
          {/* Logo compacto para móvil y tablet */}
          <div className="mb-7 flex items-center justify-between lg:hidden">
            <Logo size="sm" tagline="Estaciones de servicio" />
            <BotonPantallaCompleta />
          </div>

          <LoginCard onAuthenticated={() => navigate(from, { replace: true })} />

          <div className="mt-7 space-y-3">
            <ApiStatus />
          </div>
        </div>
      </main>
    </div>
  );
}
